import { useMemo } from "react";
import { requestStatus } from "../../../../constants";
import { getDiffDay } from "../../../../helper/dateTimeHelper";
import { useSelector } from "../../../../store/store";
import { getRequestByStatus } from "../../utils";
import { useITDepartmentRequests } from "./use-IT-department-requests";
import { useCompanyOfficeRequests } from "./use-company-office-requests";

type Request = {
  createdDateTime: Date;
  fields: {
    [key: string]: any;
  };
};

export const useLandingStatistic = () => {
  const { allITDepartmentRequests, allITDepartmentMyRequests } = useITDepartmentRequests();
  const { allCompanyOfficeRequests, allCompanyOfficeMyRequests } = useCompanyOfficeRequests();
  const user = useSelector((state) => state.user);
  const currentEmail = user?.current?.username?.toLowerCase();

  const allRequests = useMemo(
    () => [...allITDepartmentRequests, ...allCompanyOfficeRequests],
    [allITDepartmentRequests, allCompanyOfficeRequests],
  );

  const allMyRequests = useMemo(
    () => [...allITDepartmentMyRequests, ...allCompanyOfficeMyRequests],
    [allITDepartmentMyRequests, allCompanyOfficeMyRequests],
  );

  const fieldsWantToTake = [[requestStatus.completed], [requestStatus.submitted, requestStatus.preApproved]];

  const getFieldsWantToTake = (requests: Array<any>, fieldsWantToTake: Array<Array<string>>) => {
    const result: Array<any> = [];
    fieldsWantToTake.forEach((fields) => {
      result.push(getRequestByStatus(requests, fields));
    });
    return result;
  };

  const getMyAllMyRequest = (requests: any[] = []) => {
    return requests.filter((r: any) => r.createdBy.user.email === currentEmail);
  };

  const getMyAllStatusRequest = (requests: any[] = []) => {
    const myRequest = requests.filter((r: any) => r.createdBy.user.email === currentEmail);
    const resultRequests = getFieldsWantToTake(myRequest, fieldsWantToTake);
    return resultRequests;
  };

  const getApproveEmail = (eApproved: string): string => (eApproved ? eApproved.split("|")[0] : "");

  const getMyApproveRequests = (requests: Request[] = []) => {
    const user = currentEmail?.toLowerCase();

    const isApproverStatusNotApproved = (e: string, status: string) => {
      return !status && getApproveEmail(e) === user;
    };

    const isExpiredApproved = (previosApproveTime: string) =>
      getDiffDay(previosApproveTime, new Date().toDateString()) > 3;

    const approvedFields = [
      { field: "eApprovedBy", status: "eApprovedStatus", date: "createdDateTime" },
      { field: "eApprovedByITLead", status: "eApprovedByITLeadStatus", date: "eApprovedDate" },
      { field: "eApprovedByITHead", status: "eApprovedByITHeadStatus", date: "eApprovedByITLeadDate" },
      { field: "eAssignee", status: "eAssigneeStatus", date: "eApprovedByITHeadDate" },
    ];

    const newArr: Request[] = [];
    const newExpiredArray: Request[] = [];

    requests.forEach((request: Request) => {
      approvedFields.forEach(({ field, status, date }, index: number) => {
        if (index > 0 && !request.fields[approvedFields[index - 1].status]) return;
        if (isApproverStatusNotApproved(request.fields[field], request.fields[status])) {
          newArr.push(request);
          if (isExpiredApproved(index === 0 ? request.createdDateTime : request.fields[date])) {
            newExpiredArray.push(request);
          }
        }
      });
    });

    const needApproveRequests: any[] = [
      ...newArr.filter((e: any) => e.fields.eRequestStatus !== requestStatus.rejected),
    ];
    const needApproveExpiredReuqests: any[] = [
      ...newExpiredArray.filter((e: any) => e.fields.eRequestStatus !== requestStatus.rejected),
    ];

    return [needApproveRequests, needApproveExpiredReuqests];
  };

  const getMyExpiredRequests = () => {
    const isExpired = (createdDate: string) => {
      const today = new Date();
      return getDiffDay(createdDate, today.toDateString()) >= 15;
    };

    const request = getMyAllStatusRequest(allRequests)[1];
    return request.filter((e: any) => isExpired(e.createdDateTime));
  };

  const countMyFinishRequests = getMyAllStatusRequest(allRequests)[0].length;

  const countMyWaitingRequests = getMyAllStatusRequest(allRequests)[1].length;

  const countMyExpiredRequests = getMyExpiredRequests().length;

  const countMyApproveWaitingRequests = getMyApproveRequests(allRequests)[0].length;

  const countMyApproveExpiredRequests = getMyApproveRequests(allRequests)[1].length;

  const isPin = (Request: any) => {
    if (!Request) return false;
    const pins: any[] = Request.fields?.ePins ? JSON.parse(Request.fields?.ePins) : [];
    return pins.findIndex((pin: string) => pin === currentEmail) >= 0;
  };

  const isHaveAttachment = (Request: any) => {
    if (!Request || !Request.fields?.eFileContent) return false;
    const attachment: any[] = Request.fields.eFileContent ? JSON.parse(Request.fields?.eFileContent) : [];
    return attachment.length > 0;
  };

  const sortedMyRequests = getMyAllMyRequest(allRequests)
    .sort((requestA, requestB) => {
      // First, calculate the isPin value for each request
      const isPinA = isPin(requestA);
      const isPinB = isPin(requestB);

      // Compare the isPin values for sorting
      if (isPinA && !isPinB) {
        return -1; // requestA comes before requestB
      } else if (!isPinA && isPinB) {
        return 1; // requestB comes before requestA
      } else {
        return 0; // leave the order as it is
      }
    })
    .slice(0, 5);

  const sortedApproveRequests = getMyApproveRequests(allRequests)[0].sort((requestA, requestB) => {
    // First, calculate the isPin value for each request
    const isPinA = isPin(requestA);
    const isPinB = isPin(requestB);

    // Compare the isPin values for sorting
    if (isPinA && !isPinB) {
      return -1; // requestA comes before requestB
    } else if (!isPinA && isPinB) {
      return 1; // requestB comes before requestA
    } else {
      return 0; // leave the order as it is
    }
  });

  const approveFinishRequests = allMyRequests
    .sort((requestA, requestB) => {
      // First, calculate the isPin value for each request
      const isPinA = isPin(requestA);
      const isPinB = isPin(requestB);

      // Compare the isPin values for sorting
      if (isPinA && !isPinB) {
        return -1; // requestA comes before requestB
      } else if (!isPinA && isPinB) {
        return 1; // requestB comes before requestA
      } else {
        return 0; // leave the order as it is
      }
    })
    .filter((e: any) => e.fields.eRequestStatus === requestStatus.completed);

  return {
    allRequests,
    allMyRequests,
    getMyAllMyRequest,
    getMyAllStatusRequest,
    approveFinishRequests,
    getMyApproveRequests,
    getMyExpiredRequests,
    isPin,
    isHaveAttachment,
    countMyWaitingRequests,
    countMyFinishRequests,
    countMyExpiredRequests,
    countMyApproveWaitingRequests,
    countMyApproveExpiredRequests,
    sortedMyRequests,
    sortedApproveRequests,
  };
};
